// The Assertion app and terminal sign in by opening /device with a local callback:
//   /device?port=<n>&state=<random>&challenge=<base64url sha256>&device=<name>
// After Connect, the browser goes to http://127.0.0.1:<port>/callback with a one-time grant.
// The grant is useless without the verifier that never left the user's machine, which is why
// this page shows no code to compare. Old clients still open /device?code=XXXX-XXXX.
//
// The phone companion (iOS) signs in the same way but can't listen on a port: it opens
//   /device?app=ios&state=<random>&challenge=<...>&device=<name>
// and the grant goes to its own link, assertion-remote://signin, which the system hands back to
// the app that started the sign-in. Same grant, same verifier on the phone.
//
// Newer phone builds opt in to an https return instead:
//   /device?app=ios&return=https&state=<random>&challenge=<...>&device=<name>[&create=1]
// The grant goes to https://<this origin>/app/signin, a path the app's ASWebAuthenticationSession
// claims through the webcredentials entry in apple-app-site-association. Unlike a custom scheme,
// which any app can register, only our app can receive that, so the page grants straight away with
// no Connect step (see DeviceConnect.jsx). `create=1` opens Microsoft's sign-up screen.
//
// Everything here is read from the URL, i.e. attacker-controllable, so it is validated
// strictly: the callback is 127.0.0.1 with only the port from the link, the phone's fixed
// link, or this site's own /app/signin — never anything else the link names.

export const PHONE_CALLBACK = "assertion-remote://signin";
export const HTTPS_CALLBACK_PATH = "/app/signin";

export function parseAppSignin(search) {
  const p = new URLSearchParams(search || "");
  const port = p.get("port") || "";
  const phone = p.get("app") === "ios";
  const state = p.get("state") || "";
  const challenge = p.get("challenge") || "";
  const ret = p.get("return");
  if (!port && !phone && !state && !challenge) return null;
  // `return` means one thing, and only for the phone.
  if (ret !== null && !(ret === "https" && phone)) return null;
  if (!/^[A-Za-z0-9_-]{16,128}$/.test(state)) return null;
  if (!/^[A-Za-z0-9_-]{43}$/.test(challenge)) return null;
  const device = [...(p.get("device") || "")].filter((c) => c >= " ").join("").slice(0, 120);
  if (phone && port) return null;
  if (phone && ret === "https") return { app: "ios", https: true, state, challenge, device, create: p.get("create") === "1" };
  if (phone) return { app: "ios", state, challenge, device };
  const n = Number(port);
  if (!/^\d{4,5}$/.test(port) || n < 1024 || n > 65535) return null;
  return { port: n, state, challenge, device };
}

// What the https return may carry: the grant (token_urlsafe(32) on the server) or an OAuth-style
// error word. The origin is the page's own, never the link's.
const GRANT_RE = /^[A-Za-z0-9_-]{16,128}$/;
const ERROR_RE = /^[a-z_]{1,64}$/;

export function callbackUrl({ port, app, https, state }, result, origin = window.location.origin) {
  const q = new URLSearchParams({ state, ...result });
  if (app === "ios" && https) {
    const { code, error, ...rest } = result || {};
    const valid = Object.keys(rest).length === 0 && (code === undefined) !== (error === undefined)
      && (code === undefined || GRANT_RE.test(code)) && (error === undefined || ERROR_RE.test(error));
    if (!valid) throw new Error("Could not finish signing in. Start again in the Assertion app.");
    if (!/^https:\/\/[^/]+$|^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin)) {
      throw new Error("Could not finish signing in. Start again in the Assertion app.");
    }
    return `${origin}${HTTPS_CALLBACK_PATH}?${q.toString()}`;
  }
  if (app === "ios") return `${PHONE_CALLBACK}?${q.toString()}`;
  return `http://127.0.0.1:${port}/callback?${q.toString()}`;
}
